import { useState } from "react"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { SortableItem } from "lib/@components/SortableItem"
import { DragHandle } from "lib/dragHandle"
import Iconify from "minimals-template/components/Iconify"
import { RulesPanelRuleExplanation } from "./rules-panel-rule-explanation"

export function RulesPanelRuleSet({ item, index, last, canReorder, onEdit, onRemove, onMoveUp, onMoveDown }) {
    const [dragProps, setDragProps] = useState({})

    if (!item) return null

    const rules = item.rules || []
    const defaultAction = item.defaultAction === "exclude" ? "Exclude" : "Include"
    const defaultColor = item.defaultAction === "exclude" ? "error" : "success"
    const editable = typeof onEdit === "function"
    const removable = typeof onRemove === "function"
    const canMoveUp = Boolean(canReorder && index > 0)
    const canMoveDown = Boolean(canReorder && !last)

    return (
        <SortableItem id={item.id} setDragProps={setDragProps} disabled={!canReorder}>
            <Accordion
                data-cy="rules-panel-rule-set"
                disableGutters
                elevation={0}
                square
                sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                    "&:before": { display: "none" },
                }}
            >
                <AccordionSummary
                    data-cy="rules-panel-rule-set-summary"
                    expandIcon={<Iconify icon="mingcute:down-line" width={20} />}
                    sx={{ px: 1.5, "& .MuiAccordionSummary-content": { my: 1, alignItems: "center", gap: 1 } }}
                >
                    <If value={canReorder}>
                        <DragHandle {...dragProps} />
                    </If>
                    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>
                            {item.name || "Rule Set"}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                                Default action:
                            </Typography>
                            <Typography variant="caption" fontWeight={600} color={`${defaultColor}.main`}>
                                {defaultAction}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                when no rules match
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <If value={canReorder}>
                            <Tooltip title="Move rule set up">
                                <span>
                                    <IconButton
                                        data-cy="rules-panel-rule-set-move-up"
                                        size="small"
                                        disabled={!canMoveUp}
                                        onClick={handleMoveUp}
                                        aria-label="Move rule set up"
                                        sx={{ p: 0.5 }}
                                    >
                                        <Iconify icon="mdi:arrow-up" width={18} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Move rule set down">
                                <span>
                                    <IconButton
                                        data-cy="rules-panel-rule-set-move-down"
                                        size="small"
                                        disabled={!canMoveDown}
                                        onClick={handleMoveDown}
                                        aria-label="Move rule set down"
                                        sx={{ p: 0.5 }}
                                    >
                                        <Iconify icon="mdi:arrow-down" width={18} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </If>
                        <If value={editable}>
                            <Tooltip title="Edit rule set">
                                <IconButton
                                    data-cy="rules-panel-edit-rule-set"
                                    size="small"
                                    onClick={handleEdit}
                                    aria-label="Edit rule set"
                                    sx={{ p: 0.5 }}
                                >
                                    <Iconify icon="solar:pen-bold" width={18} />
                                </IconButton>
                            </Tooltip>
                        </If>
                        <If value={removable}>
                            <Tooltip title="Remove rule set">
                                <IconButton
                                    data-cy="rules-panel-remove-rule-set"
                                    size="small"
                                    color="error"
                                    onClick={handleRemove}
                                    aria-label="Remove rule set"
                                    sx={{ p: 0.5 }}
                                >
                                    <Iconify icon="mdi:trash-can-outline" width={18} />
                                </IconButton>
                            </Tooltip>
                        </If>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 1 }}>
                    <If value={rules.length === 0}>
                        <Box sx={{ px: 1.5, py: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                No rules in this set. Tasks use the default action.
                            </Typography>
                        </Box>
                        <Else>
                            <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
                                <Repeat list={rules} item={<RulesPanelRuleExplanation />} />
                            </Stack>
                        </Else>
                    </If>
                </AccordionDetails>
            </Accordion>
        </SortableItem>
    )

    function handleMoveUp(e) {
        e.stopPropagation()
        onMoveUp?.(item.id)
    }

    function handleMoveDown(e) {
        e.stopPropagation()
        onMoveDown?.(item.id)
    }

    function handleEdit(e) {
        e.stopPropagation()
        onEdit(item)
    }

    function handleRemove(e) {
        e.stopPropagation()
        onRemove(item)
    }
}
